import { Link } from "react-router-dom";
import { cn } from "@/cn";
import type { ReactNode } from "react";

const FIELD =
  "w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand";

export function fieldClassName(extra?: string) {
  return cn(FIELD, extra);
}

export function ProgressBar({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      className={cn(
        "h-1.5 w-full overflow-hidden rounded-full bg-muted",
        className,
      )}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-brand transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function StatusChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

export function FilterChips<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition",
            value === opt.value
              ? "bg-brand text-brand-foreground"
              : "bg-muted text-muted-foreground hover:bg-surface-hover hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function EntityRow({
  to,
  title,
  meta,
  trailing,
}: {
  to: string;
  title: string;
  meta?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-start justify-between gap-3 rounded-md border border-border/80 px-3 py-2.5 text-sm transition hover:border-border hover:bg-surface-hover"
      >
        <div className="min-w-0">
          <p className="font-medium text-foreground">{title}</p>
          {meta ? (
            <div className="mt-0.5 text-xs text-muted-foreground">{meta}</div>
          ) : null}
        </div>
        {trailing}
      </Link>
    </li>
  );
}
